/*======================================================================================================================
  * Bluebell: src/app/state/text-columns/text-columns.effects.ts
  * Last Edited: 29/07/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { resultsComponentLoad, resultsComponentRefresh } from '@state/results/results.actions';
import { ApiService } from '@state/api.service';
import {
  createTextColumn,
  createTextColumnFailure,
  createTextColumnSuccess,
  deleteTextColumn,
  deleteTextColumnFailure,
  deleteTextColumnSuccess,
  listTextColumns,
  listTextColumnsFailure,
  listTextColumnsSuccess,
  updateTextColumn,
  updateTextColumnFailure,
  updateTextColumnSuccess,
} from '@state/text-columns/text-columns.actions';
import { TextColumn } from '@state/text-columns/text-columns.interfaces';
import { switchMap } from 'rxjs';
import { map } from 'rxjs/operators';


@Injectable()
export class TextColumnsEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  create$ = createEffect(() => this.actions$.pipe(
    ofType(createTextColumn),
    switchMap(({ name }) => this.api.createTextColumn(name).pipe(
      this.handler.effectResponse({
        success: (column: TextColumn) => createTextColumnSuccess({ column }),
        retry: createTextColumn({ name }),
        failure: createTextColumnFailure(),
        failureMessage: 'Error Creating Text Column',
      })
    ))
  ));

  list$ = createEffect(() => this.actions$.pipe(
    ofType(listTextColumns),
    switchMap(() => this.api.listTextColumns().pipe(
      this.handler.effectResponse({
        success: (columns: TextColumn[]) => listTextColumnsSuccess({ columns }),
        retry: listTextColumns(),
        failure: listTextColumnsFailure(),
        failureMessage: 'Error Loading Text Columns',
      })
    ))
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(updateTextColumn),
    switchMap(({ uuid, name }) => this.api.updateTextColumn(uuid, name).pipe(
      this.handler.effectResponse({
        success: (column: TextColumn) => updateTextColumnSuccess({ column }),
        retry: updateTextColumn({ uuid, name }),
        failure: updateTextColumnFailure(),
        failureMessage: 'Error Updating Text Column',
      })
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(deleteTextColumn),
    switchMap(({ uuid }) => this.api.deleteTextColumn(uuid).pipe(
      this.handler.effectResponse({
        success: () => deleteTextColumnSuccess({ uuid }),
        retry: deleteTextColumn({ uuid }),
        failure: deleteTextColumnFailure(),
        failureMessage: 'Error Deleting Text Column',
        showErrorDetail: true
      })
    ))
  ));

  /**
   * Component Based Effects
   */

  // When the results component is loaded or refreshed get the text columns
  resultsComponentLoad$ = createEffect(() => this.actions$.pipe(
    ofType(resultsComponentLoad, resultsComponentRefresh),
    map(() => listTextColumns()),
  ));
}
